import { resolveCmsDevProxyUrl } from './cms-dev-proxy-url';
import {
  hasAudioPlaybackPermission,
  waitForAudioPlaybackPermission,
} from './audio-playback-permission';

export let audioManager: AudioManager | null = null;

type AudioSource = {
  audio: Audio;
  loadedUrl: string | null;
  playbackGeneration: number;
  pendingEndWait: { listener: () => void; resolve: () => void } | null;
}

export class AudioManager {
  audioSources = new Map<string, AudioSource>();

  constructor() {
    audioManager = this;
    this.audioSources.set('scene', {
      audio: new Audio(),
      loadedUrl: null,
      playbackGeneration: 0,
      pendingEndWait: null,
    });
    this.audioSources.set('audioplayer', {
      audio: new Audio(),
      loadedUrl: null,
      playbackGeneration: 0,
      pendingEndWait: null,
    });
  }

  async play(url: string, audioSourceName: 'scene' | 'audioplayer', continuePlaybackIfSameURL: boolean = false) {

    let audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    const generation = audioSource?.playbackGeneration;

    if (!hasAudioPlaybackPermission()) {
      await waitForAudioPlaybackPermission();
      if (generation !== audioSource?.playbackGeneration) return;
    }

    this.cancelPendingEndWait(audioSourceName);

    const resolvedUrl = resolveCmsDevProxyUrl(url) ?? url;
    const isSameURLLoaded = audioSource.loadedUrl === resolvedUrl;

    if (!continuePlaybackIfSameURL || !isSameURLLoaded) {
      audioSource.audio.pause();
      audioSource.audio.src = resolvedUrl;
      audioSource.loadedUrl = resolvedUrl;
    }

    try {
      await audioSource.audio.play();
    } catch (error) {
      if (generation !== audioSource?.playbackGeneration) return;
      console.warn('Audio playback blocked or failed:', error);
      return;
    }

    if (generation !== audioSource.playbackGeneration) {
      audioSource.audio.pause();
    }
  }

  waitUntilPlaybackEnded(audioSourceName: 'scene' | 'audioplayer'): Promise<void> {
    const audioSource = this.audioSources.get(audioSourceName);
    if (!audioSource) return Promise.resolve();
    if (audioSource.audio.ended) return Promise.resolve();

    this.cancelPendingEndWait(audioSourceName);

    return new Promise((resolve) => {
      const listener = () => {
        audioSource.pendingEndWait = null;
        resolve();
      };
      audioSource.pendingEndWait = { listener, resolve: listener };
      audioSource.audio.addEventListener('ended', listener, { once: true });
    });
  }

  pause(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    audioSource.audio.pause();
  }

  stop(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    audioSource.audio.pause();
    this.cancelPendingEndWait(audioSourceName);
    audioSource.audio.pause();
    try {
      audioSource.audio.currentTime = 0;
    } catch {
      /* some browsers throw when resetting before metadata */
    }
    audioSource.audio.removeAttribute('src');
    audioSource.audio.load();
    audioSource.loadedUrl = null;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('spotar:audioStopped'));
    }
  }

  private cancelPendingEndWait(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName);
    const pending = audioSource?.pendingEndWait;
    if (!pending) return;
    audioSource!.pendingEndWait = null;
    audioSource!.audio.removeEventListener('ended', pending.listener);
    pending.resolve();
  }

  isPaused(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.paused;
  }

  isPlaying(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return !audioSource.audio.paused && !this.isEnded(audioSourceName);
  }

  isEnded(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.ended;
  }

  addAudioEventListener(
    audioSourceName: 'scene' | 'audioplayer',
    event: keyof HTMLMediaElementEventMap,
    callback: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ) {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    audioSource.audio.addEventListener(event, callback, options);
  }

  removeAudioEventListener(
    audioSourceName: 'scene' | 'audioplayer',
    event: keyof HTMLMediaElementEventMap,
    callback: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions
  ) {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    audioSource.audio.removeEventListener(event, callback, options);
  }

  getAudioDuration(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.duration;
  }

  getReadyState(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.readyState;
  }

  getCurrentTime(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.currentTime;
  }
}
